<template>
	<div>
		<bar-chart :data="percentage" :labels="labels" :options="chartOptions" :series-labels="seriesLabels"
			ref="graphComponent"></bar-chart>
	</div>
</template>
<script>
import BarChart from '@/components/shared/charts/BarChart';
import ChartDataLabels from 'chartjs-plugin-datalabels';

function data() {
	return {
		dataChart: [44, 49, 48, 49, 55, 47, 43, 55, 53, 43, 44, 51],
		optionsChart: {
			responsive: true,
			legend: {
				display: false,
				position: 'right',
			},
			datasets: {
				bar: {
					categoryPercentage: 0.95,
					barPercentage: 0.8,
				},
			},
			scales: {
				yAxes: [
					{
						ticks: {
							beginAtZero: true,
							min: 0,
							max: 100,
							stepSize: 25,
							padding: 14,
							fontColor: '#487fff',
							fontStyle: 'bold',
						},
						gridLines: {
							color: '#e7e7e7',
							display: true,
							drawBorder: false,
							zeroLineColor: '#487fff',
							zeroLineWidth: 2,
						},
						position: 'right',
					},
				],
				xAxes: [
					{
						gridLines: {
							display: false,
						},
						// Configuración para manejar espacios vacíos
						offset: true,
						// Aumentar el ancho de las barras
						barPercentage: 0.7,
						categoryPercentage: 0.8,
						// Configuración para eliminar espacios en blanco
						skipNull: true,
						ticks: {
							autoSkip: false,
						},
					},
				],
			},
			tooltips: {
				enabled: true,
				mode: 'single',
				backgroundColor: 'rgba(33, 41, 52, 0.95)',
				titleFontColor: '#fff',
				titleFontSize: 14,
				bodyFontColor: 'rgba(255, 255, 255, 0.9)',
				bodyFontSize: 13,
				cornerRadius: 8,
				xPadding: 12,
				yPadding: 12,
				displayColors: false,
				bodySpacing: 8,
				caretSize: 6,
				caretPadding: 8,
				borderColor: 'rgba(255, 255, 255, 0.1)',
				borderWidth: 1,
				// Configuración para ajustar el ancho del tooltip
				titleMarginBottom: 10,
				bodyFontStyle: 'normal',
				footerFontStyle: 'normal',
				footerMarginTop: 8,
				// Asegurar que el tooltip tenga un ancho fijo
				titleAlign: 'left',
				bodyAlign: 'left',
				callbacks: {
					// Mostrar el período (Enero, Febrero, etc.)
					title: (tooltipItems, datas) => {
						if (tooltipItems && tooltipItems.length > 0) {
							const index = tooltipItems[0].index;
							return datas.labels[index] || '';
						}
						return '';
					},
					// Mostrar la serie y el valor en líneas separadas
					label: (tooltipItem, datas) => {
						// Obtener información del dataset
						const dataset = datas.datasets[tooltipItem.datasetIndex];
						const value = tooltipItem.yLabel;

						// Si el valor es NaN o null, no mostrar tooltip
						if (value === null || Number.isNaN(value)) {
							return null;
						}

						// Usar fullLabel si está disponible, sino usar el label normal
						let seriesName = dataset.fullLabel || dataset.label || 'Serie';

						// Si el nombre de la serie es muy largo, dividirlo en múltiples líneas
						// Dividir el texto en palabras
						const words = seriesName.split(' ');
						const maxCharsPerLine = 25; // Máximo de caracteres por línea
						const lines = [];
						let currentLine = '';

						// Construir las líneas
						words.forEach((word) => {
							if (`${currentLine} ${word}`.length <= maxCharsPerLine) {
								currentLine += (currentLine ? ' ' : '') + word;
							} else {
								lines.push(currentLine);
								currentLine = word;
							}
						});

						// Agregar la última línea
						if (currentLine) {
							lines.push(currentLine);
						}

						// Si hay múltiples líneas, usar esas líneas
						if (lines.length > 1) {
							seriesName = lines;
						}

						// Formatear el valor
						const formattedValue = typeof value === 'number' ? value.toFixed(2) : value;
						// Retornar un array para que cada elemento sea una línea separada
						// Si seriesName ya es un array, extenderlo con el valor
						if (Array.isArray(seriesName)) {
							return [...seriesName, `Calificación: ${parseInt(formattedValue, 10)}`];
						}
						return [seriesName, `Porcentaje: ${formattedValue}%`];
					},
					// Personalizar el renderizado del tooltip
					labelTextColor: () => '#fff',
				},
			},
			plugins: {
				datalabels: {
					display: false, // Desactivar completamente las etiquetas de datos
					color: '#487fff',
					anchor: 'end',
					align: 'top',
					font: {
						weight: 'bold',
					},
					formatter: (value, context) => {
						// No mostrar etiquetas para valores NaN o null
						if (value === null || Number.isNaN(value)) {
							return null;
						}

						const index = context.datasetIndex;
						if (context.dataset && context.dataset.fullLabel) {
							let seriesName = context.dataset.fullLabel;
							const words = seriesName.split(' ');
							const maxCharsPerLine = 25; // Máximo de caracteres por línea
							const lines = [];
							let currentLine = '';

							// Construir las líneas
							words.forEach((word) => {
								if (`${currentLine} ${word}`.length <= maxCharsPerLine) {
									currentLine += (currentLine ? ' ' : '') + word;
								} else {
									lines.push(currentLine);
									currentLine = word;
								}
							});

							// Agregar la última línea
							if (currentLine) {
								lines.push(currentLine);
							}

							// Si hay múltiples líneas, usar esas líneas
							if (lines.length > 1) {
								seriesName = lines;
							}
							return value;
						}
						return index === 0 ? `${value}%` : value;
					},
				},
			},
		},
	};
}

export default {
	data,
	components: {
		'bar-chart': BarChart,
	},
	plugins: [ChartDataLabels],
	computed: {
		chartOptions() {
			const self = this; // Capturar el contexto de this
			if (this.optionsChart.scales && this.optionsChart.scales.yAxes
				&& this.optionsChart.scales.yAxes.length > 0) {
				this.optionsChart.scales.yAxes[0].ticks.max = this.max;
			}

			// Configuración adicional para manejar valores NaN/null
			return {
				...this.optionsChart,
				// Configuración para eliminar espacios en blanco por valores nulos
				spanGaps: true,
				// Asegurar que no se muestren etiquetas de datos
				plugins: {
					...this.optionsChart.plugins,
					datalabels: {
						display: false,
					},
				},
				legend: {
					...this.optionsChart.legend,
					onHover(event, legendItem) {
						// Obtener el target del evento con validación
						const target = self.getEventTarget(event);

						// Cambiar cursor si tenemos un target válido
						if (target) {
							target.style.cursor = 'pointer';
						}

						const chart =
							self.$refs.graphComponent &&
							self.$refs.graphComponent.$data &&
							// eslint-disable-next-line
							self.$refs.graphComponent.$data._chart;

						if (
							chart &&
							chart.data &&
							chart.data.datasets &&
							legendItem.datasetIndex !== undefined
						) {
							const dataset = chart.data.datasets[legendItem.datasetIndex];

							// Mostrar tooltip solo si hay fullLabel y es diferente al texto truncado
							if (dataset && dataset.fullLabel && dataset.fullLabel !== legendItem.text) {
								self.showCustomTooltip(event, dataset.fullLabel);
							}
						}
					},

					onLeave(event) {
						// Obtener el target del evento con validación
						const target = self.getEventTarget(event);

						// Restaurar cursor si tenemos un target válido
						if (target) {
							target.style.cursor = 'default';
						}

						// Ocultar tooltip
						self.hideCustomTooltip();
					},
				},
			};
		},
	},

	methods: {
		// Helper para obtener el target del evento con validaciones
		getEventTarget(event) {
			if (event && event.target) {
				return event.target;
			} else if (event && event.native && event.native.target) {
				return event.native.target;
			}
			return null;
		},

		showCustomTooltip(event, fullText) {
			// Remover tooltip anterior si existe
			this.hideCustomTooltip();

			// Crear elemento tooltip
			const tooltip = document.createElement('div');
			tooltip.id = 'custom-legend-tooltip';
			tooltip.style.cssText = `
				position: fixed;
				background: rgba(33, 41, 52, 0.95);
				color: white;
				padding: 10px 15px;
				border-radius: 8px;
				font-size: 13px;
				max-width: 220px;
				word-wrap: break-word;
				z-index: 10000;
				pointer-events: none;
				box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
				border: 1px solid rgba(255, 255, 255, 0.1);
				line-height: 1.4;
			`;
			tooltip.textContent = fullText;

			// Obtener la posición del target usando el helper
			const target = this.getEventTarget(event);
			let rect;

			if (target) {
				rect = target.getBoundingClientRect();
			} else {
				// Posición por defecto si no podemos obtener el target
				rect = { left: 100, top: 100, bottom: 120, right: 200 };
			}

			// Posicionar tooltip
			tooltip.style.left = `${rect.left}px`;
			tooltip.style.top = `${rect.bottom + 8}px`;

			document.body.appendChild(tooltip);

			// Ajustar posición si se sale de la pantalla
			const tooltipRect = tooltip.getBoundingClientRect();

			if (tooltipRect.right > window.innerWidth) {
				tooltip.style.left = `${window.innerWidth - tooltipRect.width - 10}px`;
			}
			if (tooltipRect.bottom > window.innerHeight) {
				tooltip.style.top = `${rect.top - tooltipRect.height - 8}px`;
			}
		},
		hideCustomTooltip() {
			const existingTooltip = document.getElementById('custom-legend-tooltip');
			if (existingTooltip) {
				existingTooltip.remove();
			}
		},
	},
	beforeDestroy() {
		// Limpiar tooltip al destruir el componente
		this.hideCustomTooltip();
	},
	props: {
		average: {
			default: () => [],
			type: Array,
		},
		labels: {
			default: () => [],
			type: Array,
		},
		percentage: {
			default: () => [],
			type: Array,
		},
		seriesLabels: {
			default: () => [],
			type: Array,
		},
		max: {
			type: Number,
			default: 100,
		},
	},
};
</script>

<style>
/* Estilo para el tooltip personalizado de la leyenda */
#custom-legend-tooltip {
	width: 220px !important;
	max-width: 220px !important;
	white-space: normal !important;
	word-wrap: break-word !important;
	word-break: break-word !important;
	overflow-wrap: break-word !important;
}

/* Estilos para el tooltip de Chart.js */
.chartjs-tooltip {
	width: 220px !important;
	max-width: 220px !important;
	white-space: normal !important;
	word-wrap: break-word !important;
	word-break: break-word !important;
	overflow-wrap: break-word !important;
}

/* Flecha del tooltip (apuntando hacia abajo) */
.tooltip-arrow {
	position: absolute;
	bottom: -10px;
	left: 50%;
	margin-left: -10px;
	border-width: 10px 10px 0;
	border-style: solid;
	border-color: rgba(33, 41, 52, 0.95) transparent transparent transparent;
	width: 0;
	height: 0;
}

/* Flecha del tooltip (apuntando hacia arriba) */
.tooltip-arrow-top {
	bottom: auto !important;
	top: -10px !important;
	transform: rotate(180deg);
}

/* Estilos para los tooltips de Chart.js */
.chartjs-tooltip-key {
	display: inline-block;
	width: 10px;
	height: 10px;
	margin-right: 10px;
}

/* Estos estilos se aplicarán a los tooltips de Chart.js */
#chartjs-tooltip {
	width: 220px !important;
	max-width: 220px !important;
	white-space: normal !important;
	word-wrap: break-word !important;
	word-break: break-word !important;
	overflow-wrap: break-word !important;
}

/* Estilos para los elementos dentro del tooltip */
.chartjs-tooltip-body-item {
	white-space: normal !important;
	word-wrap: break-word !important;
	word-break: break-word !important;
	overflow-wrap: break-word !important;
	line-height: 1.5 !important;
	margin-bottom: 4px !important;
}

/* Estilos para el título del tooltip */
.chartjs-tooltip-title {
	white-space: normal !important;
	word-wrap: break-word !important;
	word-break: break-word !important;
	overflow-wrap: break-word !important;
	font-weight: bold !important;
	margin-bottom: 6px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/diagnosis/card-chart.vue